import { Specimen } from '@medplum/fhirtypes';
import { TFunction } from 'i18next';
import { IconSearch } from '@tabler/icons-react';
import { ARKON } from '@consts/systems.consts';
import { SPECIMEN_CONDITION_I18N_KEYS } from '../constants/specimen.consts';
import { getSpecimenIdSuggestions } from '../utils/fhir-helpers';
import {
  SearchFieldDefinition,
  ResourceSearchConfig,
  FhirSearchParams,
} from '@/components/resource-search/ResourceSearchForm.types';
import { ResourceTableColumn } from '@/components/resource-search/ResourceTable';

export interface SpecimenSearchParams {
  patientName?: string;
  specimenId?: string;
  specimenType?: string;
  specimenTypeDisplay?: string;
  quality?: string;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

export const SPECIMEN_SEARCH_DEFAULTS: SpecimenSearchParams = {
  patientName: undefined,
  specimenId: undefined,
  specimenType: undefined,
  specimenTypeDisplay: undefined,
  quality: undefined,
  dateFrom: null,
  dateTo: null,
};

export const specimenSearchConfig: ResourceSearchConfig<
  SpecimenSearchParams,
  Specimen
> = {
  resourceType: 'Specimen',
  buildSearchQuery: (params, page, itemsPerPage) => {
    const query: FhirSearchParams = {
      _count: itemsPerPage,
      _offset: (page - 1) * itemsPerPage,
      _sort: '-_lastUpdated',
      _include: 'Specimen:subject',
    };

    if (params.specimenId) {
      query.identifier = params.specimenId;
    }

    if (params.specimenType) {
      query.type = params.specimenType;
    }

    if (params.patientName) {
      query['subject:Patient.name'] = params.patientName;
    }

    if (params.dateFrom && params.dateTo) {
      const from = params.dateFrom.toISOString().split('T')[0];
      const to = params.dateTo.toISOString().split('T')[0];
      query._filter = `(collected ge ${from} and collected le ${to})`;
    } else if (params.dateFrom) {
      query.collected = `ge${params.dateFrom.toISOString().split('T')[0]}`;
    } else if (params.dateTo) {
      query.collected = `le${params.dateTo.toISOString().split('T')[0]}`;
    }

    return query;
  },
  needsClientSideFiltering: (params) => Boolean(params.quality),
  clientSideFilter: async (specimens, params) => {
    let results = specimens;

    if (params.quality) {
      if (params.quality === 'no-quality') {
        results = results.filter(
          (specimen) => !specimen.condition?.[0]?.coding?.[0]?.code
        );
      } else {
        results = results.filter(
          (specimen) =>
            specimen.condition?.[0]?.coding?.[0]?.code === params.quality
        );
      }
    }

    return results;
  },
  getResourceIdSuggestions: getSpecimenIdSuggestions,
};

interface SpecimenFieldOptions {
  t: TFunction<'lims'>;
  searchParams: SpecimenSearchParams;
  onSearch: (updates: Partial<SpecimenSearchParams>) => void;
  hidePatientName?: boolean;
  patientNameQuery: string;
  setPatientNameQuery: (value: string) => void;
  setPatientQuery: (value: string) => void;
  patientNameOptions: string[];
  patientLoading: boolean;
  patientNameLoading: boolean;
  specimenIdQuery: string;
  setSpecimenIdQuery: (value: string) => void;
  specimenIdOptions: string[];
  specimenIdLoading: boolean;
}

export function getSpecimenSearchFields({
  t,
  searchParams,
  onSearch,
  hidePatientName,
  patientNameQuery,
  setPatientNameQuery,
  setPatientQuery,
  patientNameOptions,
  patientLoading,
  patientNameLoading,
  specimenIdQuery,
  setSpecimenIdQuery,
  specimenIdOptions,
  specimenIdLoading,
}: SpecimenFieldOptions): SearchFieldDefinition<SpecimenSearchParams>[] {
  return [
    !hidePatientName && {
      key: 'patientName',
      type: 'autocomplete',
      placeholder: t(
        'specimensPage.searchPlaceholder',
        'Search by patient name...',
        {
          ns: 'lims',
        }
      ),
      icon: <IconSearch size={16} />,
      value: patientNameQuery,
      options: patientNameOptions,
      loading: patientLoading || patientNameLoading,
      onQueryChange: (value: string) => {
        setPatientQuery(value);
        setPatientNameQuery(value);
        if (!value) {
          onSearch({ patientName: undefined });
        }
      },
      onOptionSubmit: (value: string) => onSearch({ patientName: value }),
      onEnter: (value: string) => onSearch({ patientName: value }),
    },
    {
      key: 'specimenId',
      type: 'autocomplete',
      placeholder: t(
        'specimensPage.specimenIdPlaceholder',
        'Search by specimen ID...',
        {
          ns: 'lims',
        }
      ),
      value: specimenIdQuery,
      options: specimenIdOptions,
      loading: specimenIdLoading,
      onQueryChange: setSpecimenIdQuery,
      onOptionSubmit: (value: string) => onSearch({ specimenId: value }),
      onEnter: (value: string) => onSearch({ specimenId: value }),
      onChange: (value: string) => {
        if (!value) {
          onSearch({ specimenId: undefined });
        }
      },
    },
    {
      key: 'specimenType',
      type: 'codeableConcept',
      placeholder: t(
        'specimensPage.specimenTypePlaceholder',
        'Select specimen type...',
        {
          ns: 'lims',
        }
      ),
      binding: ARKON.VS.SPECIMEN_TYPES_EL,
      defaultValue: searchParams.specimenType
        ? {
            coding: [
              {
                code: searchParams.specimenType,
                display: searchParams.specimenTypeDisplay,
              },
            ],
          }
        : undefined,
      onChange: (value: any) => {
        const code = value?.coding?.[0]?.code || undefined;
        const display = value?.coding?.[0]?.display || value?.text || undefined;
        onSearch({
          specimenType: code,
          specimenTypeDisplay: display,
        });
      },
    },
    {
      key: 'quality',
      type: 'select',
      placeholder: t('specimensPage.qualityPlaceholder', 'Select quality...', {
        ns: 'lims',
      }),
      options: Object.keys(ARKON.CS.SPECIMEN.CONDITION.CODES).map((key) => {
        const value =
          ARKON.CS.SPECIMEN.CONDITION.CODES[
            key as keyof typeof ARKON.CS.SPECIMEN.CONDITION.CODES
          ];
        const i18nKey = SPECIMEN_CONDITION_I18N_KEYS[value];
        return {
          value,
          label: i18nKey ? t(i18nKey as any, value, { ns: 'lims' }) : value,
        };
      }),
      value: searchParams.quality,
      onChange: (value: string | null) =>
        onSearch({ quality: value || undefined }),
    },
    {
      key: 'dateFrom',
      type: 'date',
      placeholder: t('specimensPage.fromDatePlaceholder', 'From date', {
        ns: 'lims',
      }),
      value: searchParams.dateFrom,
      onChange: (value: Date | null) => onSearch({ dateFrom: value }),
    },
    {
      key: 'dateTo',
      type: 'date',
      placeholder: t('specimensPage.toDatePlaceholder', 'To date', {
        ns: 'lims',
      }),
      value: searchParams.dateTo,
      onChange: (value: Date | null) => onSearch({ dateTo: value }),
    },
  ].filter(Boolean) as SearchFieldDefinition<SpecimenSearchParams>[];
}

interface SpecimenColumnOptions {
  t: TFunction<'lims'>;
  hidePatientColumn?: boolean;
  getSpecimenId: (specimen: Specimen) => string;
  getPatientName: (specimen: Specimen) => string;
  getSpecimenType: (specimen: Specimen) => string;
  getQualityName: (specimen: Specimen) => string;
  getCollectionDate: (specimen: Specimen) => string;
}

export function getSpecimenTableColumns({
  t,
  hidePatientColumn,
  getSpecimenId,
  getPatientName,
  getSpecimenType,
  getQualityName,
  getCollectionDate,
}: SpecimenColumnOptions): ResourceTableColumn<Specimen>[] {
  const columns: ResourceTableColumn<Specimen>[] = [
    {
      key: 'specimenId',
      label: t('specimensPage.table.specimenId', 'Specimen ID', { ns: 'lims' }),
      render: (specimen) => getSpecimenId(specimen),
    },
  ];

  if (!hidePatientColumn) {
    columns.push({
      key: 'patient',
      label: t('specimensPage.table.patient', 'Patient', { ns: 'lims' }),
      render: (specimen) => getPatientName(specimen),
    });
  }

  columns.push(
    {
      key: 'type',
      label: t('specimensPage.table.type', 'Type', { ns: 'lims' }),
      render: (specimen) => getSpecimenType(specimen),
    },
    {
      key: 'quality',
      label: t('specimensPage.table.quality', 'Quality', { ns: 'lims' }),
      render: (specimen) => getQualityName(specimen),
    },
    {
      key: 'collectionDate',
      label: t('specimensPage.table.collectionDate', 'Collection Date', {
        ns: 'lims',
      }),
      render: (specimen) => getCollectionDate(specimen),
    }
  );

  return columns;
}
